import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { currentUser, logout, currentMode, setMode, currentModule, setModule } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // Check if we're on create invoice pages
  const isCreateInvoicePage = location.pathname === '/create-invoice' || location.pathname === '/create-expenses-invoice';

  // Force sidebar to be collapsed on create invoice pages
  useEffect(() => {
    if (isCreateInvoicePage) {
      setSidebarCollapsed(true);
    }
  }, [isCreateInvoicePage]);

  // Reset module when navigating to dashboard
  useEffect(() => {
    if (location.pathname === '/dashboard') {
      setModule(null);
    }
  }, [location.pathname, setModule]);

  // Ensure sidebar is visible on large screens after resizing from mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(true);
      }
    };

    // Open if page loads already at large breakpoint
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Update document title based on current route
  useEffect(() => {
    const updateDocumentTitle = () => {
      const path = location.pathname;
      
      if (path === '/create-invoice') {
        document.title = 'Create Revenue Invoice - LMS';
      } else if (path === '/create-expenses-invoice') {
        document.title = 'Create Expenses Invoice - LMS';
      } else if (path === '/edit-invoice' || path.startsWith('/edit-invoice/')) {
        document.title = 'Edit Revenue Invoice - LMS';
      } else if (path === '/edit-expenses-invoice' || path.startsWith('/edit-expenses-invoice/')) {
        document.title = 'Edit Expenses Invoice - LMS';
      } else if (path === '/all-invoices') {
        document.title = 'All Revenue Invoices - LMS';
      } else if (path === '/all-expenses-invoices') {
        document.title = 'All Expenses Invoices - LMS';
      } else if (path === '/dashboard') {
        document.title = 'Dashboard - LMS';
      } else if (path === '/reports') {
        document.title = 'Reports - LMS';
      } else if (path === '/manual-ledgers') {
        document.title = 'Manual Ledgers - LMS';
      } else if (path === '/clients') {
        document.title = 'Client Ledgers - LMS';
      } else if (path === '/company-ledgers') {
        document.title = 'Company Ledgers - LMS';
      } else if (path === '/create-client') {
        document.title = 'Create Client - LMS';
      } else if (path === '/create-company-account') {
        document.title = 'Create Company Account - LMS';
      } else if (path === '/pending-revenue-approvals') {
        document.title = 'Pending Revenue Approvals - LMS';
      } else if (path === '/pending-expense-approvals') {
        document.title = 'Pending Expenses Approvals - LMS';
      } else if (path === '/user-management') {
        document.title = 'User Management - LMS';
      } else {
        document.title = 'Accounts Management System';
      }
    };

    updateDocumentTitle();
  }, [location.pathname]);

  const getInputterNavItems = () => {
    const path = location.pathname;
    
    // Check if we're on revenue-related pages
    const isRevenuePage = 
      path === '/create-client' || 
      path === '/clients' || 
      path === '/create-invoice' || 
      path === '/all-invoices' ||
      path.includes('/edit-invoice');
    
    // Check if we're on expense-related pages
    const isExpensePage = 
      path === '/create-company-account' || 
      path === '/company-ledgers' || 
      path === '/create-expenses-invoice' || 
      path === '/all-expenses-invoices' ||
      path.includes('/edit-expenses-invoice');
    
    // Check if we're on reports page
    const isReportsPage = path === '/reports';
    
    // Reports page
    if (isReportsPage) {
      return [
        {
          name: 'Dashboard',
          icon: '🏠',
          path: '/dashboard'
        },
        {
          name: 'Reports',
          icon: '📈',
          path: '/reports'
        }
      ];
    }
    
    // Expenses module links
    if (isExpensePage) {
      return [
        {
          name: 'Dashboard',
          icon: '🏠',
          path: '/dashboard'
        },
        {
          name: 'Create Company',
          icon: '🏢',
          path: '/create-company-account'
        },
        {
          name: 'Company Ledgers',
          icon: '🏭',
          path: '/company-ledgers'
        },
        {
          name: 'Create Expenses Invoice',
          icon: '📝',
          path: '/create-expenses-invoice'
        },
        {
          name: 'All Expenses Invoices',
          icon: '📋',
          path: '/all-expenses-invoices'
        }
      ];
    }

    // Revenues module links
    if (isRevenuePage) {
      return [
        {
          name: 'Dashboard',
          icon: '🏠',
          path: '/dashboard'
        },
        {
          name: 'Create Client',
          icon: '👤',
          path: '/create-client'
        },
        {
          name: 'Client Ledgers',
          icon: '👥',
          path: '/clients'
        },
        {
          name: 'Create Revenues Invoice',
          icon: '📝',
          path: '/create-invoice'
        },
        {
          name: 'All Revenues Invoices',
          icon: '📋',
          path: '/all-invoices'
        }
      ];
    }

    // Default: only Dashboard link
    return [
      {
        name: 'Dashboard',
        icon: '🏠',
        path: '/dashboard'
      }
    ];
  };

  const inputterNavItems = getInputterNavItems();

  const getAuthorizerNavItems = () => {
    const path = location.pathname;
    
    // Check if we're on revenue-related pages
    const isRevenuePage = 
      path === '/all-invoices' || 
      path === '/pending-revenue-approvals' ||
      path.includes('/edit-invoice');
    
    // Check if we're on expense-related pages
    const isExpensePage = 
      path === '/all-expenses-invoices' || 
      path === '/pending-expense-approvals' ||
      path.includes('/edit-expenses-invoice');
    
    // Check if we're on reports page
    const isReportsPage = path === '/reports';

    // Check if we're on user management page
    const isUserManagementPage = path === '/user-management';

    // Revenue module links
    if (isRevenuePage || currentModule === 'revenue') {
      return [
        {
          name: 'Dashboard',
          icon: '🏠',
          path: '/dashboard'
        },
        {
          name: 'All Revenue Invoices',
          icon: '📋',
          path: '/all-invoices'
        },
        {
          name: 'Pending Revenue Approvals',
          icon: '✅',
          path: '/pending-revenue-approvals'
        }
      ];
    }

    // Expenses module links
    if (isExpensePage || currentModule === 'expenses') {
      return [
        {
          name: 'Dashboard',
          icon: '🏠',
          path: '/dashboard'
        },
        {
          name: 'All Expenses Invoices',
          icon: '📋',
          path: '/all-expenses-invoices'
        },
        {
          name: 'Pending Expenses Approvals',
          icon: '✅',
          path: '/pending-expense-approvals'
        }
      ];
    }

    // Reports page
    if (isReportsPage) {
      return [
        {
          name: 'Dashboard',
          icon: '🏠',
          path: '/dashboard'
        },
        {
          name: 'Reports',
          icon: '📈',
          path: '/reports'
        }
      ];
    }

    // User Management page
    if (isUserManagementPage) {
      return [
        {
          name: 'Dashboard',
          icon: '🏠',
          path: '/dashboard'
        },
        {
          name: 'User Management',
          icon: '👥',
          path: '/user-management'
        }
      ];
    }

    // Default: only Dashboard link
    return [
      {
        name: 'Dashboard',
        icon: '🏠',
        path: '/dashboard'
      }
    ];
  };

  const authorizerNavItems = getAuthorizerNavItems();

  const getNavItems = () => {
    if (currentMode === 'inputter') {
      return inputterNavItems;
    }
    return authorizerNavItems;
  };

  const navItems = getNavItems();

  return (
    <div className="min-h-screen relative">
      {/* Background Image with Blur */}
      <div 
        className="fixed inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(/assets/background.png)',
          filter: 'blur(0px)',
          transform: 'scale(1.1)'
        }}
      />
      
      {/* Overlay to darken the background */}
      <div className="fixed inset-0 bg-gray-900 bg-opacity-30" />
      
      {/* Content */}
      <div className="relative z-10">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 bg-white/60 backdrop-blur-md shadow-lg border-r border-white/20 transform transition-all duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} ${sidebarCollapsed ? 'w-16' : 'w-80'} overflow-x-hidden`}>
        <div className="flex flex-col h-full overflow-x-hidden">
          {/* Sidebar Header */}
          <div className={`flex items-center p-4 border-b border-white/20 ${sidebarCollapsed ? 'justify-center flex-col space-y-2' : 'justify-between'}`}>
            {!sidebarCollapsed ? (
              <>
                <div className="flex items-center">
                  <div className="h-10 w-10 bg-blue-800/80 backdrop-blur-sm rounded-lg flex items-center justify-center p-1.5">
                    <img src="/assets/ledger.png" alt="Accounts System" className="h-full w-full object-contain" />
                  </div>
                  <button onClick={() => navigate('/dashboard')} className="ml-3 text-left text-md font-semibold text-gray-800 hover:text-orange-800 tracking-[1px]">
                    ACCOUNTS SYSTEM
                  </button>
                </div>
                <div className="flex items-center space-x-1">
                  {/* Collapse Button - Hidden on mobile */}
                  <button
                    onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                    className="hidden lg:block p-1.5 rounded-md text-gray-800/80 hover:text-gray-800 hover:bg-white/60 backdrop-blur-sm transition-colors"
                    title="Collapse sidebar"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  {/* Close Button - Only on mobile */}
                  <button
                    onClick={() => setSidebarOpen(false)}
                    className="lg:hidden p-1 rounded-md text-gray-800/80 hover:text-gray-800 hover:bg-white/60 backdrop-blur-sm"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Logo Icon - Collapsed State */}
                <button 
                  onClick={() => navigate('/dashboard')} 
                  className="h-10 w-10 bg-blue-800/80 backdrop-blur-sm rounded-lg flex items-center justify-center hover:bg-orange-600/80 transition-colors p-1.5"
                  title="Go to Dashboard"
                >
                  <img src="/assets/ledger.png" alt="Accounts System" className="h-full w-full object-contain" />
                </button>
                {/* Expand Button - Collapsed State */}
                <button
                  onClick={() => setSidebarCollapsed(false)}
                  className="hidden lg:block p-2 rounded-md text-gray-800/80 hover:text-gray-800 hover:bg-white/60 backdrop-blur-sm transition-colors"
                  title="Expand sidebar"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}
          </div>


          {/* Navigation Items */}
          <nav className="flex-1 px-4 py-4 space-y-2 overflow-y-auto overflow-x-hidden">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              const isCreateInvoice = item.name === 'Create Revenues Invoice' || item.name === 'Create Expenses Invoice';
              
              const handleClick = () => {
                if (isCreateInvoice) {
                  // Open in new tab
                  window.open(item.path, '_blank');
                } else {
                  // Navigate normally
                  navigate(item.path);
                }
              };

              return (
                <button
                  key={item.name}
                  onClick={handleClick}
                  className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center px-2 py-3' : 'px-3 py-3'} text-xs sm:text-sm font-medium whitespace-nowrap rounded-lg transition-all duration-200 ${
                    isCreateInvoice 
                      ? `bg-gradient-to-r from-orange-200/20 to-orange-200/20 border-2 border-dashed border-orange-400/50 hover:border-orange-400 hover:from-orange-500/30 hover:to-orange-500/30 text-gray-800 hover:text-orange-600 shadow-sm hover:shadow-md backdrop-blur-sm`
                      : isActive
                        ? 'bg-orange-500/30 text-orange-500 border border-indigo-400/50 backdrop-blur-sm'
                        : 'text-gray-800/80 hover:bg-white/60 hover:text-gray-800 backdrop-blur-sm'
                  }`}
                  title={sidebarCollapsed ? item.name : ''}
                >
                  <span className={`text-sm ${sidebarCollapsed ? '' : 'mr-3'}`}>{item.icon}</span>
                  {!sidebarCollapsed && (
                    <>
                      <span className="font-medium">{item.name}</span>
                      {isCreateInvoice && (
                        <div className="ml-auto flex items-center space-x-1">
                          <span className="text-xs bg-orange-500/40 text-orange-100 px-2 py-1 rounded-full font-regular backdrop-blur-sm">
                            NEW TAB
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </button>
              );
            })}

          </nav>

          {/* Sidebar Footer */}
          <div className="p-4 border-t border-white/20">
            {!sidebarCollapsed ? (
              <>
                <div className="text-sm text-gray-800/90">
                  Welcome, <span className="font-medium">{currentUser?.name || 'User'}</span>
                </div>
                {/* Only show mode for authorizer users */}
                {currentUser?.role === 'authorizer' && (
                  <div className="text-xs text-gray-800/70 mt-1">
                    Mode: <span className="font-medium capitalize">{currentMode}</span>
                  </div>
                )}
              </>
            ) : (
              <div className="flex justify-center">
                <div className="w-8 h-8 bg-white/60 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-gray-800/90">
                    {(currentUser?.name || 'U').charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`${sidebarOpen ? (sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-80') : ''} transition-margin duration-300 ease-in-out`}>
        {/* Top Header */}
        <header className="sticky top-0 z-40 bg-white/60 backdrop-blur-md shadow-lg border-b border-white/20">
          <div className="flex items-center justify-between px-4 py-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-md text-gray-800/90 hover:text-gray-800 hover:bg-white/60 backdrop-blur-sm"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            
            <div className="flex-1"></div>
            <div className="flex items-center space-x-4">
              {/* Mode toggle - Only show for authorizer role */}
              {currentUser?.role === 'authorizer' && (
                <>
                  <div className="hidden sm:block text-xs text-gray-800/90">Mode:</div>
                  <div className="relative inline-flex items-center rounded-full border border-white/30 bg-white/60 backdrop-blur-sm shadow-lg text-xs">
                    <span
                      className={`absolute inset-y-0 left-0 w-1/2 rounded-full bg-orange-500/30 transition-transform duration-200 ease-out ${
                        currentMode === 'inputter' ? 'translate-x-0' : 'translate-x-full'
                      }`}
                    />
                    <button
                      onClick={() => setMode('inputter')}
                      className={`relative z-10 w-24 text-center px-0 py-1.5 font-medium transition-colors ${currentMode === 'inputter' ? 'text-orange-800' : 'text-gray-800/80 hover:text-gray-800'
                        }`}
                    >
                      Inputter
                    </button>
                    <button
                      onClick={() => setMode('authorizer')}
                      className={`relative z-10 w-24 text-center px-0 py-1.5 font-medium transition-colors ${currentMode === 'authorizer' ? 'text-orange-800' : 'text-gray-800/80 hover:text-gray-800'
                        }`}
                    >
                      Authorizer
                    </button>
                  </div>
                </>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center px-3 py-2 text-xs font-medium text-gray-800/90 hover:bg-white/60 hover:text-gray-800 rounded-lg transition-all duration-200 backdrop-blur-sm"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          {children}
        </main>
      </div>

        {/* Mobile overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </div>
    </div>
  );
};

export default Layout;
